
import { Component, Inject, Vue } from "vue-property-decorator";
import { Container } from "inversify";
import { OrganizationStore } from "../../../stores/organization/OrganizationStore";
import LoadingOverlay from "../../../../../common/src/components/LoadingOverlay.vue";
import { OrganizationItem } from "@gsx/common/src/types/http/dashboard/organization";
import { PaymentService } from "../../../../../landing/src/utility/payment/PaymentService";
import { NotificationStore } from "../../../../../common/src/stores/NotificationStore";
import { Api } from "../../../../../common/src/api/Api";
import { AuthStore } from "../../../../../common/src/stores/AuthStore";
import { MergerAcquisitionsCollectionStore } from "../../../stores/mergerAcquisitions/MergerAcquisitionsCollectionStore";
import { MergerAcquisitionsListStore } from "../../../stores/mergerAcquisitions/MergerAcquisitionsListStore";
import TransactionTable from "../../../molecules/transactions/TransactionTable.vue";
import ReportListItemView from "../../../molecules/reports/ReportListItem.vue";
import { handleError } from "../../../../../common/src/utility/errorUtils";
import SubscriptionUpgrade from "./SubscriptionUpgrade.vue";
import {
    CurrencyCode,
    getCurrencySymbol,
} from "../../../../../../common/src/types/models/currency";
import { QuiltyResourcesListStore } from "../../../stores/quiltyResources/QuiltyResourcesListStore";
import QuiltyResourcesListItem from "../../quiltyResources/QuiltyResourcesListItem.vue";

@Component({
    components: {
        LoadingOverlay,
        QuiltyResourcesListItem,
        TransactionTable,
        SubscriptionUpgrade,
        ReportListItemView,
    },
})
export default class FinancialInformation extends Vue {
    @Inject("container") readonly container!: Container;
    readonly organizationStore: OrganizationStore = this.container.get(OrganizationStore);
    readonly notificationStore: NotificationStore = this.container.get(NotificationStore);
    readonly api: Api = this.container.get(Api);
    readonly paymentService: PaymentService = this.container.get(PaymentService);
    readonly authStore: AuthStore = this.container.get(AuthStore);
    readonly mergerAcquisitionsListStore: MergerAcquisitionsListStore = this.container.get(
        MergerAcquisitionsListStore,
    );
    readonly mergerAcquisitionsCollectionStore: MergerAcquisitionsCollectionStore =
        this.container.get(MergerAcquisitionsCollectionStore);

    readonly quiltyResourcesListStore: QuiltyResourcesListStore =
        this.container.get(QuiltyResourcesListStore);

    notAvailable = "N/A";

    lastUrlSegment = (url: string): string => url.match(/[^\/]+(?=\/$|$)/)?.[0] ?? "UNKNOWN";

    created() {
        if (this.mergerAcquisitionsListStore.organizationId !== this.organizationId) {
            this.mergerAcquisitionsListStore.organizationId = this.organizationId;
            this.mergerAcquisitionsListStore.reload().catch(handleError);
        }
    }

    beforeUnmount() {
        this.mergerAcquisitionsListStore.organizationId = undefined;
    }

    get organizationId(): string {
        return this.$route.params.organization;
    }

    get organization(): OrganizationItem | null {
        if (this.organizationStore.item) {
            if (!this.organizationStore.item.keyPerformanceIndicators) {
                this.organizationStore.item.keyPerformanceIndicators = {
                    yearlyFinancialData: [],
                };
            }
        }

        return this.organizationStore.item;
    }

    get sortedOrgReports() {
        if (!this.organization?.reports) return [];
        return this.organization.reports.sort((a, b) => {
            const aDate = new Date(a.postDate);
            const bDate = new Date(b.postDate);

            const aTime = aDate.getTime();
            const bTime = bDate.getTime();

            return bTime - aTime;
        });
    }

    get sortedQuiltyResources() {
        if (!this.quiltyResourcesListStore.items) return [];
        return this.quiltyResourcesListStore.items.sort((a, b) => {
            const aDate = new Date(a.postDate);
            const bDate = new Date(b.postDate);

            const aTime = aDate.getTime();
            const bTime = bDate.getTime();

            return bTime - aTime;
        });
    }

    organizationUrl(organizationId: string) {
        return `/dashboard/organizations/${organizationId}`;
    }

    getFormattedTickerExchange(exchange: string | undefined, ticker: string | undefined) {
        if ((exchange && ticker) == null) {
            return this.notAvailable;
        }
        if (exchange == null) {
            return ticker;
        }
        if (ticker == null) {
            return exchange;
        }
        return exchange + ":" + ticker;
    }

    getFormattedCurrency(currency: string | undefined) {
        return currency ? "(" + currency + ")" : null;
    }

    formatCurrencyNumbers(
        input: string | undefined,
        decimalPlaces: number,
        isCurrencyAtch: boolean,
    ) {
        if (input === undefined) return;

        const num = parseFloat(input);

        if (isNaN(num)) {
            return input;
        }

        if (isCurrencyAtch) {
            return (
                num.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
                ` ${this.organization?.keyPerformanceIndicators?.currency}`
            );
        } else {
            return num.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
    }

    formatCurrencyString(input: string | undefined): string {
        if (!input) return "N/A";

        const parts = input.split(" ");

        if (parts.length !== 2) {
            return input;
        }

        const amount = parseFloat(parts[0]).toFixed(1);
        const currencyCode = parts[1] as CurrencyCode;
        const symbol = getCurrencySymbol(currencyCode);

        return `${symbol} ${amount} ${currencyCode}`;
    }
}
